/**
 * 剧本创作工作室：列表 / 保存 / 读取 / 删除（表 script_studio_projects）
 */
import api from '@/utils/api'

function pickMessage(res, fallback) {
  if (!res || typeof res !== 'object') return fallback
  const m = res.message || res.msg || res.error || (res.data && (res.data.message || res.data.msg))
  return m ? String(m) : fallback
}

/** 解开 Dingo / 业务一层或多层 data 包装，拿到带 code 的根对象 */
function unwrapRoot(res) {
  if (res == null) return null
  if (typeof res === 'string') {
    try {
      return unwrapRoot(JSON.parse(res))
    } catch {
      return null
    }
  }
  if (typeof res !== 'object') return null
  // { code, data: { code, data } }
  if (
    res.data &&
    typeof res.data === 'object' &&
    !Array.isArray(res.data) &&
    res.data.code != null &&
    res.data.data != null
  ) {
    return unwrapRoot(res.data)
  }
  return res
}

function assertApiOk(res) {
  const root = unwrapRoot(res)
  if (!root || typeof root !== 'object') {
    throw new Error('接口无有效响应')
  }
  // 未部署时常见：404 HTML/JSON 无业务 code
  if (root.code === undefined || root.code === null) {
    if (root.status_code === 404 || root.status === 404) {
      throw new Error('剧本云端接口未部署：请同步 api.php、FastMovieController，并执行 script_studio_projects.sql')
    }
    throw new Error('剧本云端接口未部署或返回异常（缺少 code）')
  }
  const n = Number(root.code)
  if (n === 0 || n === 200) return root
  throw new Error(pickMessage(root, `接口 code=${root.code}`))
}

function extractListPayload(root) {
  let data = root && root.data != null ? root.data : null
  if (data && typeof data === 'object' && !Array.isArray(data) && Array.isArray(data.list)) {
    return {
      list: data.list,
      listError: String(data.list_error || data.listError || '')
    }
  }
  // 再解一层
  if (data && typeof data === 'object' && data.data && typeof data.data === 'object') {
    const inner = data.data
    if (Array.isArray(inner.list)) {
      return {
        list: inner.list,
        listError: String(inner.list_error || inner.listError || data.list_error || '')
      }
    }
  }
  if (Array.isArray(data)) {
    return { list: data, listError: '' }
  }
  if (Array.isArray(root.list)) {
    return { list: root.list, listError: String(root.list_error || '') }
  }
  return null
}

function newUuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'ss-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10)
}

export function createScriptStudioUuid() {
  return newUuid()
}

function axiosErrorMessage(err) {
  if (!err) return '请求失败'
  const status = err.response && err.response.status
  const data = err.response && err.response.data
  if (status === 404) {
    return '剧本云端接口未部署（404）：请同步后端路由与 FastMovieController'
  }
  if (status === 401 || status === 403) {
    return '请先登录后再查看云端剧本'
  }
  if (data && typeof data === 'object') {
    const msg = pickMessage(data, '')
    if (msg) return msg
  }
  if (err.message) return String(err.message)
  return '请求失败'
}

/** GET /script_studio_projects */
export async function fetchScriptStudioList(opts) {
  const limit = opts && opts.limit != null ? opts.limit : 30
  let res
  try {
    res = await api.get('/script_studio_projects', { params: { limit } })
  } catch (err) {
    throw new Error(axiosErrorMessage(err))
  }
  const root = assertApiOk(res)
  const payload = extractListPayload(root)
  if (!payload) {
    throw new Error('剧本列表返回格式异常，请确认后端已更新 script_studio_projects 接口')
  }
  return {
    rows: payload.list,
    listError: payload.listError
  }
}

/** GET /script_studio_projects/:uuid */
export async function fetchScriptStudioDetail(uuid) {
  const id = String(uuid || '').trim()
  if (!id) throw new Error('缺少 uuid')
  let res
  try {
    res = await api.get(`/script_studio_projects/${encodeURIComponent(id)}`)
  } catch (err) {
    throw new Error(axiosErrorMessage(err))
  }
  const root = assertApiOk(res)
  let data = root.data != null ? root.data : root
  if (data && typeof data === 'object' && data.data && data.data.uuid != null) {
    data = data.data
  }
  if (!data || data.uuid == null) {
    throw new Error('项目不存在或无权访问')
  }
  return {
    uuid: data.uuid,
    title: data.title || '',
    cover_prompt: data.cover_prompt || '',
    state: data.state && typeof data.state === 'object' ? data.state : {},
    updated_at: data.updated_at || null,
    created_at: data.created_at || null
  }
}

/**
 * POST /script_studio_projects — 创建或更新
 * @param {{ uuid: string, title?: string, cover_prompt?: string, state: object }} payload
 */
export async function saveScriptStudioProject(payload) {
  const uuid = String((payload && payload.uuid) || '').trim()
  if (!uuid) throw new Error('缺少 uuid')
  let res
  try {
    res = await api.post('/script_studio_projects', {
      uuid,
      title: (payload && payload.title) || '未命名剧本',
      cover_prompt: (payload && payload.cover_prompt) || '',
      state: (payload && payload.state) || {}
    })
  } catch (err) {
    const msg = axiosErrorMessage(err)
    if (/未部署|404|保存失败|script_studio_projects\.sql/i.test(msg)) {
      throw new Error(msg)
    }
    // BadRequest 文案常在 message 里
    throw new Error(
      msg.includes('script_studio')
        ? msg
        : msg || '保存失败：请确认已执行 database/script_studio_projects.sql'
    )
  }
  const root = assertApiOk(res)
  let data = root.data != null ? root.data : {}
  if (data && typeof data === 'object' && data.data && data.data.uuid != null) {
    data = data.data
  }
  return {
    uuid: (data && data.uuid) || uuid,
    title: (data && data.title) || '',
    updated_at: (data && data.updated_at) || null
  }
}

/** DELETE /script_studio_projects/:uuid */
export async function deleteScriptStudioProject(uuid) {
  const id = String(uuid || '').trim()
  if (!id) throw new Error('缺少 uuid')
  let res
  try {
    res = await api.delete(`/script_studio_projects/${encodeURIComponent(id)}`)
  } catch (err) {
    throw new Error(axiosErrorMessage(err))
  }
  assertApiOk(res)
}
